import isEqual from 'react-fast-compare';
import { IDocumentTable } from '@models/Forms/IForms';
import { ValueType } from '@srm/ui/types/ValueType';
import { loadMathJs } from '@utils/mathjsLoader';

type Row = Record<string, unknown>;
type Evaluate = (formula: string, row: Row, parent?: Row) => Promise<unknown>;

const MAX_PASSES = 5;

/** Форма монтирует только видимые поля, а вычисляемые значения сохраняются
 * и для скрытых колонок. Пересчёт не повторяет changes и события строк. */
export const calculateRowFormTree = async (
    table: IDocumentTable,
    source: Row,
    evaluate: Evaluate,
    parent?: Row,
): Promise<Row> => {
    const row = { ...source };
    const calculate = async () => {
        for (const column of table.tableColumnCalc ?? []) {
            if (!column.formula) continue;
            const value = await evaluate(column.formula, row, parent);
            if (column.valueType === ValueType.Text) {
                row[column.key] = value;
            } else {
                const converted = Number(value);
                const number = Number.isNaN(converted) ? 0 : converted;
                row[column.key] = column.floatPoints ? (await loadMathJs()).round(number, column.floatPoints) : number;
            }
        }
    };
    // Дети читают уже вычисленные поля Parent, а агрегаты родителя —
    // окончательные вычисленные значения детей. Если пересчёт родителя по детям
    // изменил его поля (total = SUM(детей), а дети считают долю от Parent|total),
    // дети пересчитываются ещё раз; предел проходов страхует от формул-качелей.
    await calculate();
    for (let pass = 0; pass < MAX_PASSES; pass++) {
        let childrenChanged = false;
        for (const child of table.tables ?? []) {
            const children = row[child.key];
            if (!Array.isArray(children)) continue;
            const calculated: Row[] = [];
            for (const childRow of children) {
                calculated.push(await calculateRowFormTree(child, childRow as Row, evaluate, row));
            }
            if (!isEqual(children, calculated)) {
                row[child.key] = calculated;
                childrenChanged = true;
            }
        }
        if (!childrenChanged) break;
        const before = { ...row };
        await calculate();
        if (isEqual(before, row)) break;
    }
    return row;
};
